import { Toast } from 'primereact/toast';
import { useEffect, useRef, useState } from 'react';
import { Toolbar } from 'primereact/toolbar';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { FilterMatchMode, FilterOperator } from 'primereact/api';
import { Tooltip } from 'primereact/tooltip';
import { Badge } from 'primereact/badge';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { Checkbox } from 'primereact/checkbox';
import { Avatar } from 'primereact/avatar';
import { Dialog } from 'primereact/dialog';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import moment from 'moment';
import { Check, Copy } from 'lucide-react';

import AddNote from '../../components/AddNote/addNote';
import ConfirmStatusModal from '../../components/ConfirmStatusModal/ConfirmStatusModal';
import ConfirmTaxFileProcessedByModal from '../../components/ConfirmTaxFileProcessedByModal/ConfirmTaxFileProcessedByModal';
import EditEmail from '../../components/EditEmail/EditEmail';
import ChangeTaxFillingDateModal from '../../components/ChangeTaxFillingDateModal/ChangeTaxFillingDateModal';
import AppBreadcrumb from '../../components/CustomeBreadcrumb/AppBreadcrumb';
import FileUploader from '../../components/FileUploader/FileUploader';
import CopyAllButton from '../../components/CopyAllButton/CopyAllButton';

import {
    defaultPageRowOptions,
    defaultRowOptions,
    paginatorLinks,
    showingEntries,
    SkeletonbodyTemplate,
    Skeletonitems,
    needReviewStatusDropdown,
} from '../../appconfig/Settings';
import { TaxListColumns } from '../../appconfig/DatatableSetting';
import {
    getAllDetails,
    getSpecificDetails,
    postAllData,
    postDataByID,
    updateData,
} from '../../service/ApiService';
import {
    CHANGE_PAYMENT_STATUS,
    GET_GLOBAL_DATA,
    GET_LIST_EMPLOYEE,
    NEED_REVIEW_STATUS_CHANGE,
    POST_SIGNED_DOC,
    RESEND_BALANCE_AMOUNT_PAYMENT_LINK,
    RESEND_SIGN_DOC_EMAIL,
    SEND_DOCUMENT_NOTE_EMAIL,
    UPDATE_TAX_FILING_DATES,
    UPDATE_TAXPAYER_NOTE,
    UPDATE_TAXPAYER_EMAIL,
} from '../../config/ApiConstant';
import {
    addressTemplate,
    dateTemplate,
} from '../../appconfig/TemplateFieldSetting';
import {
    validateSignAndReadingFileUpload,
    validateTaxFillingDates,
    validateResendBalanceAmount,
    validateEditEmail,
} from '../../config/Validate';

const emptyFilters = {
    global: {
        value: null,
        matchMode: FilterMatchMode.CONTAINS,
    },
    note: {
        operator: FilterOperator.OR,
        constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }],
    },
    tax_form_status: {
        operator: FilterOperator.OR,
        constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }],
    },
    first_time_form_filling_status: {
        operator: FilterOperator.OR,
        constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }],
    },
    slot_booking_status: {
        operator: FilterOperator.OR,
        constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }],
    },
    email: {
        operator: FilterOperator.OR,
        constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
    },
    phone: {
        operator: FilterOperator.OR,
        constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
    },
};

interface CommonTaxFileListProps {
    fetchData: (searchStr?: string) => Promise<any[]>;
    pageTitle: string;
    breadcrumbMenu: string;
    storagePrefix: string;
    emptyMessage: string;
    extraToolbarLeft?: React.ReactNode;
    extraToolbarRight?: React.ReactNode;
    refreshDependencies?: any[];
}

const CommonTaxFileList: React.FC<CommonTaxFileListProps> = ({
    fetchData,
    pageTitle,
    breadcrumbMenu,
    storagePrefix,
    emptyMessage,
    extraToolbarLeft,
    extraToolbarRight,
    refreshDependencies = []
}) => {
    const toast = useRef<any>(null);
    const location = useLocation();
    const navigate = useNavigate();

    // Global search filter value state
    const [globalFilter, setGlobalFilter] = useState<any>(() => {
        return localStorage.getItem(`${storagePrefix}Search`) || null;
    });

    // Pagination starting index state
    const [first, setFirst] = useState<number>(() => {
        const savedFirst = localStorage.getItem(`${storagePrefix}First`);
        return savedFirst ? parseInt(savedFirst, 10) : 0;
    });
    // Rows per page count state
    const [rows, setRows] = useState<number>(() => {
        const savedRows = localStorage.getItem(`${storagePrefix}Rows`);
        return savedRows ? parseInt(savedRows, 10) : defaultRowOptions;
    });

    // loading
    // Page data loading status state
    const [pageLoad, setPageLoad] = useState<boolean>(false);
    // Submission process loading state
    const [submitLoading, setSubmitLoading] = useState<boolean>(false);

    // list
    // Tax form records list state
    const [taxFormList, setTaxFormList] = useState<any>([]);

    // dialog states
    // Selected employee ID state
    const [activeEmployeeId, setActiveEmployeeId] = useState<any>(null);
    // Selected status value state
    const [selectedDropdownStatus, setSelectedDropdownStatus] = useState<any>(null);
    // Status modal visibility state
    const [formStatusDialog, setFormStatusDialog] = useState<boolean>(false);
    // Email notification status state
    const [email_status, setEmail_status] = useState<boolean>(true);

    // Upload modal visibility state
    const [fileUploadDialog, setFileUploadDialog] = useState<boolean>(false);
    // Selected upload file state
    const [selectedFile, setSelectedFile] = useState<any>(null);
    // Selection for reading-only file
    const [readingFileSelected, setReadingFileSelected] = useState<any>(null);
    // Selected spouse file state
    const [spouseFile, setSpouseFile] = useState<any>(null);
    // Spouse reading file selection state
    const [spouseReadingFileSelected, setSpouseReadingFileSelected] = useState<any>(null);
    // Upload record details state
    const [fileUploadData, setFileUploadData] = useState<any>();
    // File upload status flag
    const [fileUploadStatus, setFileUploadStatus] = useState<any>(null);
    // Display balance amount state
    const [balanceAmountDisplay, setBalanceAmountDisplay] = useState<any>();
    // Income document for upload
    const [uploadDocIncome, setUploadDocIncome] = useState<any>();
    // Balance payment status flag
    const [isETransfer, setIsETransfer] = useState<boolean>(false);
    const [isBalanceAmountPaid, setIsBalanceAmountPaid] = useState<boolean>(false);

    //copy phone number
    // WhatsApp link copy flag
    const [whastappLinkCopy, setWhastappLinkCopy] = useState<boolean>(false);
    // Read doc copy flag
    const [whastappLinkReadDocCopy, setWhastappLinkReadDocCopy] = useState<boolean>(false);
    // Spouse link copy flag
    const [whastappLinkSpouseCopy, setWhastappLinkSpouseCopy] = useState<boolean>(false);
    // Spouse read copy flag
    const [whastappLinkReadDocSpouseCopy, setWhastappLinkReadDocSpouseCopy] = useState<boolean>(false);


    // Filer modal visibility state
    const [taxFiledByDialog, setTaxFiledByDialog] = useState<boolean>(false);
    // Selected tax filer state
    const [selectedTaxFiledBy, setSelectedTaxFiledBy] = useState<any>(null);
    // Employee dropdown options state
    const [employeeListOptions, setEmployeeListOptions] = useState<any>([]);

    // Note modal visibility state
    const [addNote, setAddNote] = useState<boolean>(false);
    // Note content text state
    const [noteText, setNoteText] = useState<string>('');
    // Note saving loading state
    const [noteLoading, setNoteLoading] = useState<boolean>(false);

    // Balance modal visibility state
    const [balanceAmountModal, setBalanceAmountModal] = useState<boolean>(false);
    // Balance record ID state
    const [balanceAmountId, setBalanceAmountId] = useState<any>(null);
    // Balance operation loading state
    const [balanceAmountLoader, setBalanceAmountLoader] = useState<boolean>(false);

    // Resend modal visibility state
    const [resendMailLinkDialog, setResendMailLinkDialog] = useState<boolean>(false);
    // Resend link ID state
    const [resendMailLinkId, setResendMailLinkId] = useState<any>(null);
    // Resend email loading state
    const [resendMailLinkLoader, setResendMailLinkLoader] = useState<boolean>(false);
    // Taxpayer resend flag state
    const [isTaxpayerResend, setIsTaxpayerResend] = useState<boolean>(false);
    // Spouse resend flag state
    const [isSpouseResend, setIsSpouseResend] = useState<boolean>(false);

    // Date modal visibility state
    const [taxFilingDateChangeDialog, setTaxFilingDateChangeDialog] = useState<boolean>(false);
    // Selected filing dates state
    const [taxFilingDate, setTaxFilingDate] = useState<Date[]>([]);
    // Enabled filing days state
    const [taxFilingDays, setTaxFilingDays] = useState<any>();

    // Email modal visibility state
    const [editEmailDialog, setEditEmailDialog] = useState<boolean>(false);
    // Email dialog data state
    const [globleEmailDialogData, setGlobleEmailDialogData] = useState<any>({});
    // Email validation errors state
    const [emailErrors, setEmailErrors] = useState<any>({});

    // E-transfer modal visibility state
    const [isETransferModal, setIsETransferModal] = useState<boolean>(false);
    // E-transfer loading state
    const [ETransferLoading, setETransferLoading] = useState<boolean>(false);
    // E-transfer payment ID state
    const [ETransferBalanceId, setETransferBalanceId] = useState<any>(null);

    // Generated WhatsApp links state
    const [whatAppLink, setWhatAppLink] = useState<any>(null);
    // WhatsApp modal visibility state
    const [whatAppLinkDialog, setWhatAppLinkDialog] = useState<boolean>(false);

    // Currently copied link key state
    const [copiedLinkKey, setCopiedLinkKey] = useState<string | null>(null);
    // Copied record ID state
    const [copiedId, setCopiedId] = useState<number | null>(null);
    // Validation errors state
    const [errors, setErrors] = useState<any>({});

    const [noteFilter, setNoteFilter] = useState<string>(() => {
        return localStorage.getItem(`${storagePrefix}NoteFilter`) || 'all';
    });

    // Document note email modal state
    const [emailNoteDialog, setEmailNoteDialog] = useState<boolean>(false);
    // Note text for document email
    const [emailNoteText, setEmailNoteText] = useState<string>('');
    // Loading state for email note submit
    const [emailNoteLoading, setEmailNoteLoading] = useState<boolean>(false);
    // Active row data for email note
    const [emailNoteRowData, setEmailNoteRowData] = useState<any>(null);

    const dataTableRef = useRef<any>(null);
    const topScrollRef = useRef<any>(null);

    // Data table filters state
    const [filters, setFilters] = useState<any>(() => {
        const savedFilters = localStorage.getItem(`${storagePrefix}Filters`);
        if (savedFilters) {
            try {
                return { ...emptyFilters, ...JSON.parse(savedFilters) };
            } catch (e) {
                return emptyFilters;
            }
        }
        return emptyFilters;
    });

    // Save filters to local storage
    useEffect(() => {
        localStorage.setItem(`${storagePrefix}Filters`, JSON.stringify(filters));
    }, [filters, storagePrefix]);

    // Load initial data on mount
    useEffect(() => {
        getTaxFormList();
        getEmployeeList();
        if (!sessionStorage.getItem(`${storagePrefix}ScrollPos`)) {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }, [storagePrefix, ...refreshDependencies]);

    // Restore scroll position after load
    useEffect(() => {
        const scrollStr = sessionStorage.getItem(`${storagePrefix}ScrollPos`);
        if (pageLoad && taxFormList?.length > 0 && scrollStr) {
            const targetPos = parseInt(scrollStr, 10);
            let attempts = 0;
            const interval = setInterval(() => {
                window.scrollTo({ top: targetPos, behavior: 'smooth' });
                attempts++;
                if (document.body.scrollHeight > targetPos || attempts >= 15) {
                    clearInterval(interval);
                    sessionStorage.removeItem(`${storagePrefix}ScrollPos`);
                }
            }, 100);
            return () => clearInterval(interval);
        }
    }, [pageLoad, taxFormList, storagePrefix]);

    // Fetch employee list from API
    const getEmployeeList = async () => {
        try {
            const response = await getAllDetails(GET_LIST_EMPLOYEE + '?role=Employee');
            if (response) {
                let employeedata = response?.map((item: any) => ({
                    label: `${item?.first_name ?? ''} ${item?.last_name ?? ''}`.trim(),
                    value: item?.id,
                    disabled: item?.status === false || item?.status === 0
                }));
                setEmployeeListOptions(employeedata);
            }
        } catch (error: any) {
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: error.response?.data?.message || 'Failed to fetch employee list',
            });
        }
    };

    // Fetch tax records from API
    const getTaxFormList = async (
        searchStr?: string,
        noteFilterValue?: string
    ) => {
        setPageLoad(false);

        try {
            const query = searchStr !== undefined ? searchStr : globalFilter;

            let data = await fetchData(query);

            const selectedNoteFilter =
                noteFilterValue !== undefined ? noteFilterValue : noteFilter;

            // Sort only when "data_with_notes" selected
            if (
                selectedNoteFilter === 'data_with_notes' &&
                data &&
                data.length > 0
            ) {
                data = data.sort((item1: any, item2: any) => {
                    const note1 =
                        item1.note && item1.note.trim() !== '' ? 1 : 0;

                    const note2 =
                        item2.note && item2.note.trim() !== '' ? 1 : 0;

                    return note2 - note1;
                });
            }

            setTaxFormList(data || []);
            setPageLoad(true);
        } catch (error: any) {
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail:
                    error.response?.data?.message || 'Failed to fetch list',
            });

            setPageLoad(false);
        }
    };

    // Handle global search input change
    const onGlobalFilterChange = (e: any) => {
        const value = e.target.value;
        setGlobalFilter(value);
        if (!value) {
            setFirst(0);
            localStorage.setItem(`${storagePrefix}First`, '0');
            localStorage.removeItem(`${storagePrefix}Search`);
            getTaxFormList('');
        }
    };

    // Execute search with global filter
    const onSearchClick = () => {
        const searchValue = globalFilter?.trim();
        setFirst(0);
        localStorage.setItem(`${storagePrefix}First`, '0');
        if (searchValue) {
            localStorage.setItem(`${storagePrefix}Search`, searchValue);
            getTaxFormList(searchValue);
        } else {
            localStorage.removeItem(`${storagePrefix}Search`);
            getTaxFormList('');
        }
    };

    // Copy text to clipboard
    const handleCopy = async (
        text: string,
        id: any = null,
        type: "sign" | "read" | "spouseSign" | "spouseRead" | null = null
    ) => {
        await navigator.clipboard.writeText(text);

        // table phone copy
        if (id !== null) {
            setCopiedId(id);
        }

        // sign link copy
        if (type === "sign") {
            setWhastappLinkCopy(true);
        }

        // read document copy
        if (type === "read") {
            setWhastappLinkReadDocCopy(true);
        }

        // spouse sign link copy
        if (type === "spouseSign") {
            setWhastappLinkSpouseCopy(true)
        }

        // spouse read document copy
        if (type === "spouseRead") {
            setWhastappLinkReadDocSpouseCopy(true)
        }

        setTimeout(() => {
            setCopiedId(null);
            setWhastappLinkCopy(false);
            setWhastappLinkReadDocCopy(false);
            setWhastappLinkSpouseCopy(false);
            setWhastappLinkReadDocSpouseCopy(false);
        }, 5000);
    };

    // Copy all document URLs
    // Copy all document URLs feedback
    const handleCopyAllFinish = () => {
        setWhastappLinkCopy(true);
        setWhastappLinkReadDocCopy(true);
        setWhastappLinkSpouseCopy(true);
        setWhastappLinkReadDocSpouseCopy(true);

        setTimeout(() => {
            setWhastappLinkCopy(false);
            setWhastappLinkReadDocCopy(false);
            setWhastappLinkSpouseCopy(false);
            setWhastappLinkReadDocSpouseCopy(false);
        }, 5000);
    };

    // Reset all filters and pagination
    const clearAll = () => {
        setFilters({ ...emptyFilters });
        setGlobalFilter(null);

        setNoteFilter('all');

        localStorage.removeItem(`${storagePrefix}First`);
        localStorage.removeItem(`${storagePrefix}Rows`);
        localStorage.removeItem(`${storagePrefix}Search`);
        localStorage.removeItem(`${storagePrefix}Filters`);
        localStorage.removeItem(`${storagePrefix}NoteFilter`);

        setFirst(0);
        setRows(defaultRowOptions);

        getTaxFormList('', 'all');
    };

    // Save window scroll position
    const saveScrollPosition = () => {
        sessionStorage.setItem(`${storagePrefix}ScrollPos`, window.scrollY.toString());
    };

    // Templates
    // Render employee name column
    const employeeDetailTemplate = (rowData: any) => {
        const fullName = `${rowData?.first_name || ''} ${rowData?.middle_name || ''} ${rowData?.last_name || ''}`.trim();
        const displayName = fullName.length > 20 ? fullName.substring(0, 20) + '...' : fullName || '-';
        const targetClass = `name-tooltip-${rowData.id}`;

        return (
            <NavLink to={`/tax-payer-detail/${btoa(rowData.id)}`} onClick={saveScrollPosition}>
                <div className="avatar-div flex align-items-center">
                    <Avatar icon="pi pi-user" shape="circle" />
                    <div className="emp-name link-name ml-2">
                        {fullName.length > 20 ? (
                            <>
                                <div className={`name ${targetClass}`} data-pr-tooltip={fullName} data-pr-position="bottom">
                                    {displayName}
                                </div>
                                <Tooltip target={`.${targetClass}`} />
                            </>
                        ) : (
                            <div className="name">{displayName}</div>
                        )}
                    </div>
                </div>
            </NavLink>
        );
    };

    // Render email address column
    const emailTemplate = (rowData: any) => {
        const email = rowData?.email || "";
        const displayEmail = email.length > 20 ? email.substring(0, 20) + "..." : email || "-";
        const targetClass = `email-tooltip-${rowData.id}`;
        return (
            <div style={{ display: "flex", alignItems: "center" }}>
                {email.length > 20 ? (
                    <>
                        <div className={`name ${targetClass}`} data-pr-tooltip={email} data-pr-position="bottom">
                            {displayEmail}
                        </div>
                        <Tooltip target={`.${targetClass}`} />
                    </>
                ) : (
                    <div className="name">{displayEmail}</div>
                )}
            </div>
        );
    };

    const noteFilterTemplate = (options: any) => {
        const dropdownOptions = [
            { label: 'All', value: 'all' },
            { label: 'Data With Notes', value: 'data_with_notes' },
        ];

        return (
            <Dropdown
                value={options.value || noteFilter}
                options={dropdownOptions}
                optionLabel="label"
                optionValue="value"
                placeholder="Select"
                className="p-column-filter"
                onChange={(e) => {
                    options.filterCallback(e.value);
                    setNoteFilter(e.value);
                    localStorage.setItem(`${storagePrefix}NoteFilter`, e.value);
                    getTaxFormList(undefined, e.value);
                }}
            />
        );
    };

    const noteFilterFunction = () => {
        return true;
    };

    // Render phone number column
    const phoneNumberTemplate = (rowData: any) => {
        const isCopied = copiedId === rowData.id;
        const targetClass = `phone-copy-tooltip-${rowData.id}`;
        return (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span>{rowData.phone}</span>
                <span className={targetClass} style={{ cursor: "pointer", display: "flex" }}
                    onClick={async () => {
                        await navigator.clipboard.writeText(rowData.phone);
                        setCopiedId(rowData.id);
                        setTimeout(() => setCopiedId(null), 5000);
                    }}
                    data-pr-tooltip={isCopied ? "Copied" : "Copy"}
                    data-pr-position="top">
                    {isCopied ? <Check size={16} color="#22c55e" /> : <Copy size={16} color="#6b7280" />}
                </span>
                <Tooltip target={`.${targetClass}`} key={isCopied ? "copied" : "copy"} />
            </div>
        );
    };

    const statusColors: Record<string, string> = {
        'Form Submitted': '#2563eb',
        'Tax Filed': '#16a34a',
        'Missing Document/Information': '#dc2626',
        'Need Review': '#f59e0b',
        'Sign Pending': '#9333ea',
        'Sign Done': '#565c5a',
        'Whatsapp': '#25D366',
        'Document Received': '#0ea5e9',
        'Sign Done Whatsapp': '#10b981',
        'Ready To File': '#664835',
    };

    // Render status dropdown item
    const statusItemTemplate = (option: any) => (
        <span style={{ color: statusColors[option.value], fontWeight: "bold" }}>{option.label}</span>
    );

    // Render status dropdown value
    const statusValueTemplate = (option: any, props: any) => {
        if (!option) return <span>{props.placeholder}</span>;
        const displayLabel = option.label && option.label.length > 12 ? option.label.substring(0, 12) + "..." : option.label;
        return (
            <span title={option.label} style={{ color: statusColors[option.value], fontWeight: "bold" }}>
                {displayLabel}
            </span>
        );
    };

    // Render status selection column
    const formStatusTemplate = (rowData: any) => {
        return (
            <Dropdown
                value={rowData.tax_form_status}
                options={needReviewStatusDropdown}
                placeholder="Select Status"
                className="w-14rem"
                onChange={(e) => {
                    setActiveEmployeeId(rowData.id);
                    setSelectedDropdownStatus(e.value);
                    setFormStatusDialog(true);
                }}
                itemTemplate={statusItemTemplate}
                valueTemplate={statusValueTemplate}
            />
        );
    };

    // Close file upload modal
    const hideFileUploadDialog = () => {
        setFileUploadDialog(false);
        setActiveEmployeeId(null);
        setIsETransfer(false);
        setIsBalanceAmountPaid(false);
        setSelectedFile(null);
        setErrors(null);
        setFileUploadData(null);
        setFileUploadStatus(null);
        setReadingFileSelected(null);
        setSpouseFile(null);
        setSpouseReadingFileSelected(null);
        setUploadDocIncome(null);
    };

    // close whatsapp link dialog
    // Close WhatsApp link modal
    const hideWhatAppLinkDialog = () => {
        setWhatAppLinkDialog(false);
        setWhatAppLink(null);
        setWhastappLinkCopy(false);
        setWhastappLinkReadDocCopy(false);
        setWhastappLinkSpouseCopy(false);
        setWhastappLinkReadDocSpouseCopy(false);
    };

    // Render tax filer column
    const taxFilledByUserName = (rowData: any) => {
        const isDisabled = [
            'Sign Done',
            'Sign Pending',
            'Tax Filed',
            'Sign Done Whatsapp',
            'Ready To File',
        ].includes(rowData.tax_form_status);

        const employeeValueTemplate = (option: any, props: any) => {
            if (!option) return <span>{props.placeholder}</span>;
            const displayLabel = option.label && option.label.length > 10 ? option.label.substring(0, 10) + '...' : option.label;
            return <span title={option.label}>{displayLabel}</span>;
        };

        return (
            <Dropdown
                value={rowData?.tax_filed_by ?? null}
                options={employeeListOptions}
                optionLabel="label"
                optionValue="value"
                optionDisabled="disabled"
                placeholder="Select Employee"
                disabled={isDisabled}
                onChange={(e) => {
                    setActiveEmployeeId(rowData?.id);
                    setSelectedTaxFiledBy(e.value);
                    setTaxFiledByDialog(true);
                }}
                valueTemplate={employeeValueTemplate}
            />
        );
    };

    // Render balance payment column
    const balancePaymentTemplate = (rowData: any) => {
        const paymentData = rowData?.payment_data?.[0];
        const tempPaymentData = rowData?.temp_payment_data?.[0];

        if (paymentData) {
            return (
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span>{paymentData.amount}</span>
                        {paymentData.e_transfer_payment === true && (
                            <Checkbox
                                checked={paymentData.status === 'Completed'}
                                disabled={paymentData.status === 'Completed'}
                                onChange={() => {
                                    setIsETransferModal(true);
                                    setETransferBalanceId(paymentData.id);
                                }}
                            />
                        )}
                    </div>
                    {paymentData.e_transfer_payment === false && (
                        <div style={{ marginTop: '4px' }}>
                            <Badge
                                value={paymentData.status}
                                severity={paymentData.status === 'Completed' ? 'success' : paymentData.status === 'Pending' ? 'warning' : 'danger'}
                            />
                        </div>
                    )}
                </div>
            );
        }

        if (tempPaymentData) {
            return (
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                    {tempPaymentData.amount}
                    <div style={{ marginTop: '4px' }}>
                        <Badge
                            value={tempPaymentData.status}
                            severity={tempPaymentData.status === 'Completed' ? 'success' : tempPaymentData.status === 'Pending' ? 'warning' : 'danger'}
                        />
                    </div>
                </div>
            );
        }
        return '-';
    };

    // Render notes action column
    const notesTemplate = (rowData: any) => {
        let notes = rowData?.note || 'Add Notes';
        return (
            <div>
                <Button
                    tooltip={notes}
                    tooltipOptions={{ position: 'bottom' }}
                    className="ml-2"
                    style={{
                        backgroundColor: rowData?.note ? 'red' : '#2563eb',
                        borderColor: rowData?.note ? 'red' : '#2563eb',
                    }}
                    icon="pi pi-comment"
                    onClick={() => {
                        setActiveEmployeeId(rowData?.id);
                        setNoteText(rowData?.note || '');
                        setAddNote(true);
                    }}
                />
            </div>
        );
    };

    // Render first-time status column
    const firstTimeFormFillingStatusTemplate = (rowData: any) => (
        <div className="name">
            <Badge
                severity={rowData?.first_time_form_filling_status ? 'success' : undefined}
                value={rowData?.first_time_form_filling_status ? 'Yes' : 'No'}
                style={!rowData?.first_time_form_filling_status ? { backgroundColor: '#A9A9A9', color: '#fff' } : {}}
                className="mt-1"
            />
        </div>
    );

    // Render signed PDF column
    const signDocumentTemplate = (rowData: any) => {
        const hasTaxpayerDoc = !!rowData?.tax_payer_signed_document?.[0]?.signed_url;
        const hasSpouseDoc = !!rowData?.tax_payer_spouse_signed_document?.[0]?.signed_url;
        const viewPdf = (url: string) => window.open(url, '_blank');

        return (
            <div className='flex justify-content-center'>
                {hasTaxpayerDoc && (
                    <Button icon="pi pi-file-pdf" tooltip="View Signed PDF" className="p-button-secondary ml-2 common-view-pdf-btn"
                        tooltipOptions={{ position: 'bottom' }} onClick={() => viewPdf(rowData?.tax_payer_signed_document[0].signed_url)} />
                )}
                {hasSpouseDoc && (
                    <Button icon="pi pi-file-pdf" tooltip="View Spouse Signed PDF" className="p-button-secondary ml-2 common-view-pdf-btn"
                        tooltipOptions={{ position: 'bottom' }} onClick={() => viewPdf(rowData?.tax_payer_spouse_signed_document[0].signed_url)} />
                )}
                {!hasTaxpayerDoc && !hasSpouseDoc && '-'}
            </div>
        );
    };

    // Render file upload column
    const uploadFileTemplate = (rowData: any) => {

        const hasSpouse = (rowData.is_spouse_canadian_resident == 1 && (rowData.dec_marrige_status == 'Married' || rowData.dec_marrige_status == "Living common-law")) &&
            ((rowData.file_spouse_tax == null && rowData.data_added_manually == 0) ||
                (rowData.file_spouse_tax == 1 && rowData.data_added_manually == 1));
        return (
            <div>
                <Button
                    tooltip={'Upload Doc'}
                    tooltipOptions={{ position: 'bottom' }}
                    className={`ml-2 common-primary-button ${hasSpouse ? 'purple-upload-btn' : ''}`}
                    style={hasSpouse ? { backgroundColor: 'purple', borderColor: 'purple' } : {}}
                    icon="pi pi-file-edit"
                    disabled={rowData?.docu_sign_env_id != null}
                    onClick={() => {
                        setActiveEmployeeId(rowData?.id);
                        setFileUploadDialog(true);
                        setFileUploadData(rowData);
                        setFileUploadStatus(0);
                        setIsBalanceAmountPaid(rowData?.payment_data && rowData?.payment_data.length > 0 ? true : false);
                        setIsETransfer(rowData?.e_transfer_payment ? true : false);
                        setBalanceAmountDisplay(rowData?.balance_amount);
                    }}
                />
            </div>
        );
    }

    // Render row actions column
    const actionBodyTemplate = (rowData: any) => (
        <div className="actions">
            <Button
                icon="pi pi-calendar"
                className="common-primary-button mr-2"
                tooltip="Change Date"
                tooltipOptions={{ position: 'bottom' }}
                onClick={async () => {
                    try {
                        const response = await getAllDetails(GET_GLOBAL_DATA);
                        setTaxFilingDays(response?.no_of_days_enable);
                    } catch (error) { }
                    setActiveEmployeeId(rowData?.id);
                    if (rowData?.slot?.date) {
                        const parsedDates = JSON.parse(rowData.slot.date);
                        setTaxFilingDate(parsedDates.map((d: string) => moment(d, 'YYYY-MM-DD').toDate()));
                    } else {
                        setTaxFilingDate([]);
                    }
                    setTaxFilingDateChangeDialog(true);
                }}
            />
            <Button
                icon="pi pi-pencil"
                tooltip="Edit Email"
                className="common-edit-btn"
                tooltipOptions={{ position: 'bottom' }}
                onClick={() => {
                    setGlobleEmailDialogData({ ...rowData });
                    setEditEmailDialog(true);
                }}
            />
            <Button
                icon="pi pi-dollar"
                tooltip="Resend Balance Payment Link"
                className="common-edit-btn ml-2"
                tooltipOptions={{ position: 'bottom' }}
                disabled={rowData?.payment_data && rowData?.payment_data.length > 0}
                onClick={() => {
                    setBalanceAmountId(rowData?.id);
                    setBalanceAmountModal(true);
                }}
            />
            <Button
                icon="pi pi-envelope"
                tooltip="Resend Mail Link"
                className="common-edit-btn ml-2"
                tooltipOptions={{ position: 'bottom' }}
                disabled={!rowData?.document_uploaded_status}
                onClick={() => {
                    setResendMailLinkId(rowData?.id);
                    setResendMailLinkDialog(true);
                    setIsTaxpayerResend(true);
                    setIsSpouseResend((rowData?.is_spouse_canadian_resident === 'true') && (rowData?.file_spouse_tax === "true" || rowData?.file_spouse_tax === 'true'));
                    setFileUploadData(rowData);
                }}
            />
        </div>
    );

    // Handle file input changes
    const onInputFileChange = (e: any, type: 'main' | 'reading' | 'spouse_main' | 'spouse_reading') => {
        let val = e?.files;
        if (e?.files) {
            val = Array.isArray(e.files) ? e.files : e.files;
        }

        // document to be signed
        if (type === 'main') {
            setSelectedFile(val);
        }

        // document selected for reading
        if (type === 'reading') {
            setReadingFileSelected(val);
        }

        // spouse document to be signed
        if (type === 'spouse_main') {
            setSpouseFile(val);
        }

        // spouse document selected for reading
        if (type === 'spouse_reading') {
            setSpouseReadingFileSelected(val);
        }

        setErrors(null);
    };

    // Render booking date column
    const bookingDateTemplate = (rowData: any) => {
        if (!rowData?.slot?.date) return '-';
        return (
            <div className="actions">
                <Button
                    icon="pi pi-calendar"
                    className="common-primary-button ml-2"
                    tooltip="View Calendar Dates"
                    tooltipOptions={{ position: 'bottom' }}
                    onClick={async () => {
                        try {
                            const response = await getAllDetails(GET_GLOBAL_DATA);
                            setTaxFilingDays(response?.no_of_days_enable);
                        } catch (error) { }
                        setActiveEmployeeId(rowData?.id);
                        if (rowData?.slot?.date) {
                            const parsedDates = JSON.parse(rowData.slot.date);
                            setTaxFilingDate(parsedDates.map((d: string) => moment(d, 'YYYY-MM-DD').toDate()));
                        }
                        setTaxFilingDateChangeDialog(true);
                    }}
                />
            </div>
        );
    };

    // Render booking status column
    const slotBookingStatusTemplate = (rowData: any) => {
        let statusClass: any = rowData.slot_booking_status === false ? "warning" : "success";
        return (
            <div className="actions flex flex-column align-items-center">
                <Badge severity={statusClass} value={rowData.slot_booking_status === false ? "Pending" : "Booked"}
                    style={{ width: "fit-content" }} className="mt-1" />
            </div>
        );
    };

    // Render required document note email column
    const emailNoteTemplate = (rowData: any) => {
        const hasNote = !!rowData?.email_note;
        return (
            <div className='flex justify-content-center'>
                <Button
                    icon="pi pi-send"
                    tooltip={hasNote ? rowData.email_note : 'Send Document Request'}
                    tooltipOptions={{ position: 'bottom' }}
                    className={hasNote ? 'p-button-success' : 'common-primary-button'}
                    onClick={() => {
                        setEmailNoteRowData(rowData);
                        setEmailNoteText(rowData?.email_note || '');
                        setEmailNoteDialog(true);
                    }}
                />
            </div>
        );
    };

    // Render GST calc column
    const gstCalcTemplate = (rowData: any) => {
        const hasGst = !!rowData.gst_data?.id;
        return (
            <Button
                icon={hasGst ? 'pi pi-eye' : 'pi pi-plus'}
                tooltip={hasGst ? 'View GST Calculation' : 'Add GST Calculation'}
                tooltipOptions={{ position: 'bottom' }}
                className={hasGst ? 'p-button-success' : 'common-primary-button'}
                onClick={() => {
                    if (hasGst) {
                        navigate(`/gst-calc/view/${btoa(String(rowData.gst_data.id))}`);
                    } else {
                        const fullName = [rowData.first_name, rowData.middle_name, rowData.last_name].filter(Boolean).join(' ');
                        navigate('/gst-calc/add', {
                            state: {
                                name: fullName,
                                sin_number: rowData.sin_number || '',
                                mobile_no: rowData.phone || '',
                                tax_form_id: rowData.id,
                            },
                        });
                    }
                }}
            />
        );
    };

    // Submits
    // Update form status via API
    const updateFormStatusApiCall = async () => {
        try {
            setSubmitLoading(true);
            const payload: any = { status: selectedDropdownStatus };
            if (selectedDropdownStatus === 'Tax Filed') payload.email_status = email_status ? '1' : '0';
            const response = await updateData(NEED_REVIEW_STATUS_CHANGE, activeEmployeeId, payload);
            if (response) {
                setFormStatusDialog(false);
                setSelectedDropdownStatus(null);
                toast.current?.show({ severity: 'success', summary: 'Success', detail: response?.message });
                getTaxFormList();
            }
        } catch (error: any) {
            toast.current?.show({ severity: 'error', summary: 'Alert', detail: error?.response?.data?.message });
        } finally {
            setSubmitLoading(false);
        }
    };

    // Update tax filer via API
    const updateTaxFiledByApiCall = async () => {
        try {
            setSubmitLoading(true);
            const response = await updateData(UPDATE_TAXPAYER_NOTE, activeEmployeeId, { tax_filed_by: selectedTaxFiledBy });
            if (response) {
                setTaxFiledByDialog(false);
                setSelectedTaxFiledBy(null);
                toast.current?.show({ severity: 'success', summary: 'Success', detail: response?.message || 'Tax filed by updated successfully' });
                getTaxFormList();
            }
        } catch (error: any) {
            toast.current?.show({ severity: 'error', summary: 'Alert', detail: error?.response?.data?.message || 'Failed to update' });
        } finally {
            setSubmitLoading(false);
        }
    };

    // Save taxpayer note via API
    const saveNoteHandler = async () => {
        try {
            setNoteLoading(true);
            const response = await updateData(UPDATE_TAXPAYER_NOTE, activeEmployeeId, { note: noteText });
            if (response) {
                toast.current?.show({ severity: 'success', summary: 'Success', detail: 'Notes added successfully' });
                setAddNote(false);
                getTaxFormList();
            }
        } catch (error: any) {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: error?.response?.data?.message || 'Failed to save note' });
        } finally {
            setNoteLoading(false);
        }
    };

    // Resend payment link via API
    const resendBalanceAmountSubmit = async () => {
        try {
            const { errors, isError } = validateResendBalanceAmount(uploadDocIncome);
            setErrors(errors);
            if (isError) return;
            setBalanceAmountLoader(true);
            const response = await postAllData(RESEND_BALANCE_AMOUNT_PAYMENT_LINK, { id: balanceAmountId, balance_amount: uploadDocIncome });
            if (response) {
                setBalanceAmountModal(false);
                setUploadDocIncome(null);
                toast.current?.show({ severity: 'success', summary: 'Success', detail: response?.message });
                getTaxFormList();
            }
        } catch (error: any) {
            toast.current?.show({ severity: 'error', summary: 'Alert', detail: error?.response?.data?.message });
        } finally {
            setBalanceAmountLoader(false);
        }
    };
    // filter status dropdown
    // Render status filter dropdown
    const formStatusDropdown = (options: any) => {
        return (
            <Dropdown
                value={options.value}
                options={needReviewStatusDropdown}
                itemTemplate={statusItemTemplate}
                onChange={(e) => options.filterCallback(e.value)}
                optionLabel="name"
                placeholder={options.value ? options.value : 'Select Status'}
                className="p-column-filter"
                style={{ minWidth: '14rem' }}
            />
        );
    };

    // Resend sign email via API
    const resendMailLinkSubmit = async () => {
        try {
            setResendMailLinkLoader(true);
            const payload = {
                id: resendMailLinkId,
                tax_payer_email_status: isTaxpayerResend ? '1' : '0',
                spouse_email_status: isSpouseResend ? '1' : '0',
            };
            const response = await updateData(RESEND_SIGN_DOC_EMAIL, resendMailLinkId, payload);
            if (response) {
                setResendMailLinkDialog(false);
                setResendMailLinkId(null);
                toast.current?.show({ severity: 'success', summary: 'Success', detail: response?.message });
                getTaxFormList();
            }
        } catch (error: any) {
            toast.current?.show({ severity: 'error', summary: 'Alert', detail: error?.response?.data?.message });
        } finally {
            setResendMailLinkLoader(false);
        }
    };

    // Update filing dates via API
    const updateTaxFilingDate = async () => {
        try {
            const { isError, errors } = validateTaxFillingDates(taxFilingDate);
            setErrors(errors);
            if (isError) return;
            setSubmitLoading(true);
            const response = await updateData(UPDATE_TAX_FILING_DATES, activeEmployeeId, { date: taxFilingDate.map((d: Date) => moment(d).format('YYYY-MM-DD')) });
            if (response) {
                setTaxFilingDateChangeDialog(false);
                toast.current?.show({ severity: 'success', summary: 'Success', detail: response?.message });
                getTaxFormList();
            }
        } catch (error: any) {
            toast.current?.show({ severity: 'error', summary: 'Alert', detail: error?.response?.data?.message });
        } finally {
            setSubmitLoading(false);
        }
    };

    // Confirm e-transfer via API
    const eTransferPayment = async () => {
        try {
            setETransferLoading(true);
            const response = await postDataByID(CHANGE_PAYMENT_STATUS, ETransferBalanceId);
            if (response) {
                getTaxFormList();
                toast.current?.show({ severity: 'success', summary: 'Success', detail: response.message });
                setIsETransferModal(false);
            }
        } catch (error: any) {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: error?.response?.data?.message });
        } finally {
            setETransferLoading(false);
        }
    };

    // Upload documents via API
    const fileUploadSubmit = async () => {
        try {
            let isSpouseReq = false;
            try {
                isSpouseReq = (fileUploadData?.is_spouse_canadian_resident == 1 && fileUploadData?.file_spouse_tax == null && fileUploadData?.data_added_manually == 0) ||
                    (fileUploadData?.file_spouse_tax == 1 && fileUploadData?.data_added_manually == 1);
            } catch (e) { isSpouseReq = false; }

            const { errors, isError } = validateSignAndReadingFileUpload(selectedFile, readingFileSelected, spouseFile, spouseReadingFileSelected, isSpouseReq);
            setErrors(errors);
            if (isError) return;
            setSubmitLoading(true);

            const formData: any = new FormData();
            if (uploadDocIncome !== null && uploadDocIncome !== undefined && fileUploadStatus == 0) formData.append('balance_amount', uploadDocIncome);
            formData.append('is_spouse_sign_doc', fileUploadStatus);
            if (isETransfer === true) formData.append('e_transfer_payment', isETransfer);

            const appendFiles = (key: string, files: any) => {
                if (!files) return;
                if (Array.isArray(files)) files.forEach(f => formData.append(key, f));
                else formData.append(key, files);
            };

            appendFiles('taxpayer_file', selectedFile);
            appendFiles('taxpayer_read_file', readingFileSelected);
            if (isSpouseReq) {
                appendFiles('spouse_file', spouseFile);
                appendFiles('spouse_read_file', spouseReadingFileSelected);
            }

            const response = await updateData(POST_SIGNED_DOC, activeEmployeeId, formData);
            if (response) {
                setWhatAppLink({ ...response?.data, e_transfer_payment: isETransfer, balance_amount: uploadDocIncome });
                setWhatAppLinkDialog(true);
                setFileUploadDialog(false);
                setUploadDocIncome(null);
                setSelectedFile(null);
                setReadingFileSelected(null);
                setSpouseFile(null);
                setSpouseReadingFileSelected(null);
                toast.current?.show({ severity: 'success', summary: 'Success', detail: response?.message });
                getTaxFormList();
            }
        } catch (error: any) {
            toast.current?.show({ severity: 'error', summary: 'Alert', detail: error?.response?.data?.message });
        } finally {
            setSubmitLoading(false);
        }
    };

    // Render left toolbar content
    const leftToolbarTemplate = () => (
        <>
            <span className="block mr-2 p-input-icon-left">
                <i className="pi pi-search" />
                <InputText type="search" value={globalFilter || ''} onChange={onGlobalFilterChange} onKeyDown={(e) => e.key === 'Enter' && onSearchClick()} placeholder="Search..." />
            </span>
            <Button className="common-primary-button mr-2" onClick={onSearchClick}>Search</Button>
            {extraToolbarLeft}
        </>
    );

    // Render right toolbar content
    const rightToolbarTemplate = () => (
        <>
            {extraToolbarRight}
            <Button icon="pi pi-filter-slash" label="Clear" onClick={clearAll} className="ml-3 p-button-outlined common-p-outline-button" />
        </>
    );

    // Sync table and top scrollbars
    useEffect(() => {
        const tableEl = dataTableRef.current?.getElement();
        if (!tableEl) return;
        const wrapper = tableEl.querySelector('.p-datatable-scrollable-body') || tableEl.querySelector('.p-datatable-wrapper');
        const topScroll = topScrollRef.current;
        if (wrapper && topScroll) {
            const updateDummyWidth = () => {
                const table = wrapper.querySelector('table');
                if (table) {
                    const dummyContent = topScroll.querySelector('.dummy-content');
                    if (dummyContent) dummyContent.style.width = table.scrollWidth + 'px';
                }
            };
            updateDummyWidth();
            const handleTopScroll = (e: any) => { wrapper.scrollLeft = e.target.scrollLeft; };
            const handleBottomScroll = (e: any) => { topScroll.scrollLeft = e.target.scrollLeft; };
            topScroll.addEventListener('scroll', handleTopScroll);
            wrapper.addEventListener('scroll', handleBottomScroll);
            let resizeObserver: ResizeObserver | null = null;
            if (typeof ResizeObserver !== 'undefined' && wrapper.querySelector('table')) {
                resizeObserver = new ResizeObserver(() => updateDummyWidth());
                resizeObserver.observe(wrapper.querySelector('table'));
            }
            return () => {
                topScroll.removeEventListener('scroll', handleTopScroll);
                wrapper.removeEventListener('scroll', handleBottomScroll);
                if (resizeObserver) resizeObserver.disconnect();
            };
        }
    }, [taxFormList, pageLoad]);

    return (
        <>
            <AppBreadcrumb currentMenu={breadcrumbMenu} pageName={pageTitle} pageTitle={pageTitle} />
            <Toast ref={toast} />
            <div className="grid crud-demo mt-4">
                <div className="col-12">
                    <div className="card">
                        <style>
                            {`
                .purple-upload-btn:hover { background-color: #800080 !important; border-color: #800080 !important; }
                .top-scrollbar { overflow-x: auto; overflow-y: hidden; height: 20px; width: 100%; }
                .dummy-content { height: 1px; }
                .hide-horizontal-scrollbar .p-datatable-wrapper,
                .hide-horizontal-scrollbar .p-datatable-scrollable-body,
                .hide-horizontal-scrollbar .p-datatable-scrollable-wrapper,
                .hide-horizontal-scrollbar .p-datatable-scrollable-view {
                  scrollbar-width: none !important;
                  -ms-overflow-style: none !important;
                }
                .hide-horizontal-scrollbar .p-datatable-wrapper::-webkit-scrollbar:horizontal,
                .hide-horizontal-scrollbar .p-datatable-scrollable-body::-webkit-scrollbar:horizontal,
                .hide-horizontal-scrollbar .p-datatable-scrollable-wrapper::-webkit-scrollbar:horizontal,
                .hide-horizontal-scrollbar .p-datatable-scrollable-view::-webkit-scrollbar:horizontal {
                  display: none !important;
                  height: 0 !important;
                  width: 0 !important;
                }
                .p-datatable .p-datatable-thead > tr > th {
                  white-space: normal !important;
                  word-wrap: break-word !important;
                  text-align: center !important;
                }
                .p-datatable .p-datatable-thead > tr > th .p-column-title {
                  white-space: normal !important;
                }
                   .crud-demo .p-datatable .p-datatable-wrapper .p-datatable-thead > tr > th {
                    padding : 5px !important;
                }
                .crud-demo .p-datatable .p-datatable-wrapper .p-datatable-tbody > tr > td {
                  padding : 5px !important;
                     display: flex !important;
                     /* justify-content: center !important; */
                 }
                .purple-upload-btn:hover {
                  background-color: #800080 !important;
                  border-color: #800080 !important;
                }
                .p-dialog-mask .p-dialog .p-dialog-content {
                    overflow-x: hidden !important;
                }
              `}
                        </style>
                        <Toolbar left={leftToolbarTemplate} right={rightToolbarTemplate}></Toolbar>

                        <div ref={topScrollRef} className="top-scrollbar">
                            <div className="dummy-content"></div>
                        </div>

                        {pageLoad ? (
                            <DataTable
                                ref={dataTableRef}
                                value={taxFormList || []}
                                showGridlines
                                dataKey="id"
                                responsiveLayout={"scroll"}
                                stripedRows
                                rows={rows}
                                first={first}
                                onPage={(e: any) => {
                                    setFirst(e.first);
                                    setRows(e.rows);
                                    localStorage.setItem('taxListFirst', String(e.first));
                                    localStorage.setItem('taxListRows', String(e.rows));
                                }}
                                paginator={(taxFormList?.length || 0) > 0}
                                rowsPerPageOptions={defaultPageRowOptions}
                                className="datatable-responsive p-datatable-sm hide-horizontal-scrollbar"
                                paginatorTemplate={paginatorLinks}
                                currentPageReportTemplate={showingEntries + ` ${pageTitle}`}
                                emptyMessage={emptyMessage}
                                removableSort
                                scrollable
                                filters={filters}
                                onFilter={(e: any) => {
                                    setFilters(e.filters);
                                    setFirst(0);
                                    localStorage.setItem('taxListFirst', '0');
                                }}
                                globalFilterFields={["name", "email"]}
                            >
                                {TaxListColumns.map((col) => {
                                    const props: any = { key: col.field, field: col.field, header: col.header };
                                    const filterableFields = ["email", "tax_form_status", "first_time_form_filling_status"];
                                    if (filterableFields.includes(col.field)) {
                                        props.filter = true;
                                        props.filterPlaceholder = `Search by ${col.header}`;
                                    }

                                    if (col.field === "name") return <Column {...props} body={employeeDetailTemplate} frozen alignFrozen="left" style={{ minWidth: "14rem", zIndex: 1, display: "flex", justifyContent: "flex-start" }} />;
                                    if (col.field === "tax_filled_by") return <Column {...props} body={taxFilledByUserName} style={{ minWidth: "12rem", display: "flex", justifyContent: "center" }} />;
                                    if (col.field === "balance_payment") return <Column {...props} body={balancePaymentTemplate} style={{ minWidth: "8rem", display: "flex", justifyContent: "center" }} />;
                                    if (col.field === "action") return <Column {...props} body={actionBodyTemplate} style={{ minWidth: "12rem", display: "flex", justifyContent: "center" }} />;
                                    if (col.field === "email") return <Column {...props} body={emailTemplate} style={{ minWidth: "14rem", display: "flex", justifyContent: "center" }} />;
                                    if (col.field === "gst_data") return <Column {...props} body={gstCalcTemplate} style={{ minWidth: "6rem", display: "flex", justifyContent: "center" }} />;
                                    if (col.field === "upload_doc") return <Column {...props} body={uploadFileTemplate} style={{ minWidth: "8rem", display: "flex", justifyContent: "center" }} />;
                                    if (col.field === 'signed_doc') return <Column {...props} body={signDocumentTemplate} style={{ minWidth: '8rem', display: 'flex', justifyContent: 'center' }} />;
                                    if (col.field === 'email_note') return <Column {...props} body={emailNoteTemplate} style={{ minWidth: '9rem', display: 'flex', justifyContent: 'center' }} />;
                                    if (col.field === "phone") return <Column {...props} body={phoneNumberTemplate} style={{ minWidth: "8rem", display: "flex", justifyContent: "center" }} />;
                                    if (col.field === "date_of_birth") return <Column {...props} body={dateTemplate} style={{ minWidth: "20rem", display: "flex", justifyContent: "center" }} />;
                                    if (col.field === "booked_date") return <Column {...props} body={bookingDateTemplate} style={{ minWidth: "20rem", display: "flex", justifyContent: "center" }} />;
                                    if (col.field === "note")
                                        return (
                                            <Column
                                                {...props}
                                                body={notesTemplate}
                                                filter
                                                filterElement={noteFilterTemplate}
                                                filterFunction={noteFilterFunction}
                                                showFilterMatchModes={false}
                                                style={{
                                                    minWidth: "6rem",
                                                    display: "flex",
                                                    justifyContent: "center"
                                                }}
                                            />
                                        );
                                    if (col.field === "first_time_form_filling_status") return <Column {...props} body={firstTimeFormFillingStatusTemplate} style={{ minWidth: "6rem", display: "flex", justifyContent: "center" }} />;
                                    if (col.field === "address") return <Column {...props} body={addressTemplate} style={{ minWidth: "20rem", display: "flex", justifyContent: "center" }} />;
                                    if (col.field === "slot_booking_status") return <Column {...props} body={slotBookingStatusTemplate} style={{ minWidth: "10rem", display: "flex", justifyContent: "center" }} />;
                                    if (col.field === "tax_form_status") return <Column {...props} body={formStatusTemplate} filterElement={formStatusDropdown} style={{ minWidth: "11rem", display: "flex", justifyContent: "center" }} />;
                                    return <Column {...props} style={{ minWidth: "20rem", display: "flex", justifyContent: "center" }} />;
                                })}
                            </DataTable>
                        ) : (
                            <DataTable value={Skeletonitems} showGridlines responsiveLayout="scroll" scrollable className="hide-horizontal-scrollbar">
                                {TaxListColumns.map((col) => (
                                    <Column key={col.field} field={col.field} header={col.header} body={SkeletonbodyTemplate} style={{ minWidth: "14rem" }} />
                                ))}
                            </DataTable>
                        )}
                    </div>
                </div>
            </div>

            {/* Modal to add taxpayer notes */}
            <AddNote addNote={addNote} hideNotesDialog={() => setAddNote(false)} saveNoteHandler={saveNoteHandler} noteText={noteText} setNoteText={setNoteText} noteLoading={noteLoading} />

            {/* Modal to confirm status change */}
            <ConfirmStatusModal visible={formStatusDialog} onHide={() => setFormStatusDialog(false)} onConfirm={updateFormStatusApiCall} loading={submitLoading} selectedStatus={selectedDropdownStatus} email_status={email_status} setEmail_status={setEmail_status} />

            {/* Modal to confirm tax filer */}
            <ConfirmTaxFileProcessedByModal visible={taxFiledByDialog} onHide={() => setTaxFiledByDialog(false)} onConfirm={updateTaxFiledByApiCall} loading={submitLoading} employeeName={employeeListOptions?.find((e: any) => e.value === selectedTaxFiledBy)?.label ?? ''} />

            {/* Modal for document uploads */}
            <Dialog
                visible={fileUploadDialog}
                style={{ width: '720px' }}
                header="Upload Document"
                className="mx-3 lg:mx-0"
                draggable={false}
                modal
                footer={
                    <>
                        <Button
                            label="Cancel"
                            icon="pi pi-times"
                            className="p-button-secondary"
                            onClick={hideFileUploadDialog}
                        />
                        <Button
                            label="Send"
                            icon="pi pi-send"
                            className="common-primary-button"
                            onClick={fileUploadSubmit}
                            loading={submitLoading}
                        />
                    </>
                }
                onHide={hideFileUploadDialog}
            >
                <div className="grid ">
                    {fileUploadData !== 'spouse' && (
                        <>
                            <div className="field col-12 md:col-6">
                                <label htmlFor="spouse_income_from_home">
                                    Enter Balance Amount
                                </label>
                                <div className="flex-1 input-effect-div p-inputgroup">
                                    <span className="font-bold p-inputgroup-addon">$</span>
                                    <InputNumber
                                        id="income"
                                        value={uploadDocIncome}
                                        onChange={(e) => setUploadDocIncome(e.value)}
                                        placeholder="Enter Amount"
                                        minFractionDigits={2}
                                        maxFractionDigits={2}
                                        mode="decimal"
                                    // disabled={isBalanceAmountPaid}
                                    />
                                </div>
                            </div>
                            <div className="col-6" style={{ marginTop: '32px' }}>
                                <Checkbox
                                    inputId="e_transfer_payment"
                                    className="ml-2"
                                    checked={isETransfer == true}
                                    onChange={(e) => setIsETransfer(e.checked)}
                                />
                                <label className="ml-2">E-Transfer Payment</label>
                            </div>
                        </>
                    )}

                    <div className="field col-12 md:col-12">
                        <p style={{ color: 'red' }}>
                            <span style={{ fontSize: '18px', fontWeight: '600' }}>
                                Note:
                            </span>{' '}
                            Upload the document to be signed by the taxpayer, which will
                            be sent via email.
                        </p>
                        <div>
                            {/* Taxpayer Documents */}
                            <div style={{ marginBottom: "25px" }}>
                                <h4 style={{ fontWeight: "bold", marginBottom: "10px" }}>
                                    Taxpayer Documents
                                </h4>

                                <div className="flex gap-4">
                                    <div className="flex-1">
                                        <label>
                                            Upload T183 <span className="required">*</span>
                                        </label>
                                        <FileUploader
                                            name="file_upload"
                                            acceptedFiles=".pdf"
                                            onInputChange={(e: any) => onInputFileChange(e, "main")}
                                            multiple={false}
                                            maxSize={10}
                                            value={selectedFile}
                                        />
                                        <small className="p-invalid-txt">{errors?.selectedFile}</small>
                                    </div>

                                    <div className="flex-1">
                                        <label>
                                            Upload Tax Summary <span className="required">*</span>
                                        </label>
                                        <FileUploader
                                            name="reading_file"
                                            acceptedFiles=".pdf"
                                            onInputChange={(e: any) => onInputFileChange(e, "reading")}
                                            multiple={false}
                                            maxSize={10}
                                            value={readingFileSelected}
                                        />
                                        <small className="p-invalid-txt">{errors?.readingFileSelected}</small>
                                    </div>
                                </div>
                            </div>

                            {/* Spouse Documents */}
                            {(() => {
                                let showSpouse = false;
                                try {
                                    const hasSpouse = (fileUploadData?.is_spouse_canadian_resident == 1 && (fileUploadData?.dec_marrige_status == 'Married' || fileUploadData?.dec_marrige_status == "Living common-law")) &&
                                        ((fileUploadData?.file_spouse_tax == null && fileUploadData?.data_added_manually == 0) ||
                                            (fileUploadData?.file_spouse_tax == 1 && fileUploadData?.data_added_manually == 1));
                                    showSpouse = hasSpouse;
                                } catch (e) {
                                    showSpouse = false;
                                }
                                return showSpouse;
                            })() && (
                                    <div>
                                        <h4 style={{ fontWeight: "bold", marginBottom: "10px" }}>
                                            Spouse Documents
                                        </h4>

                                        <div className="flex gap-4">
                                            <div className="flex-1">
                                                <label>
                                                    Upload Spouse T183 <span className="required">*</span>
                                                </label>
                                                <FileUploader
                                                    name="spouse_file_upload"
                                                    acceptedFiles=".pdf"
                                                    onInputChange={(e: any) =>
                                                        onInputFileChange(e, "spouse_main")
                                                    }
                                                    multiple={false}
                                                    maxSize={10}
                                                    value={spouseFile}
                                                />
                                                <small className="p-invalid-txt">{errors?.spouseFile}</small>
                                            </div>

                                            <div className="flex-1">
                                                <label>
                                                    Upload Spouse Tax Summary <span className="required">*</span>
                                                </label>
                                                <FileUploader
                                                    name="spouse_reading_file"
                                                    acceptedFiles=".pdf"
                                                    onInputChange={(e: any) =>
                                                        onInputFileChange(e, "spouse_reading")
                                                    }
                                                    multiple={false}
                                                    maxSize={10}
                                                    value={spouseReadingFileSelected}
                                                />
                                                <small className="p-invalid-txt">
                                                    {errors?.spouseReadingFileSelected}
                                                </small>
                                            </div>
                                        </div>
                                    </div>
                                )}
                        </div>
                    </div>
                </div>
            </Dialog>

            {/* Modal to resend balance link */}
            <Dialog visible={balanceAmountModal} style={{ width: '450px' }} header="Resend Balance Payment Link" modal onHide={() => setBalanceAmountModal(false)}
                footer={<><Button label="Cancel" icon="pi pi-times" className="p-button-secondary" onClick={() => setBalanceAmountModal(false)} /><Button label="Send" icon="pi pi-send" className="common-primary-button" onClick={resendBalanceAmountSubmit} loading={balanceAmountLoader} /></>}>
                <div className="grid">
                    <div className="field col-12">
                        <label htmlFor="balance_amount">Enter Balance Amount</label>
                        <div className="input-effect-div p-inputgroup">
                            <span className="font-bold p-inputgroup-addon">$</span>
                            <InputNumber id="balance_amount" value={uploadDocIncome} onValueChange={(e) => setUploadDocIncome(e.value)} placeholder="Enter Amount" minFractionDigits={2} maxFractionDigits={2} className="w-full h-3rem font-bold" />
                        </div>
                        {errors?.balance_amount && <span className="p-error">{errors.balance_amount}</span>}
                    </div>
                </div>
            </Dialog>

            {/* Modal to resend sign link */}
            <Dialog visible={resendMailLinkDialog} style={{ width: '450px' }} header="Resend Mail Link" modal onHide={() => setResendMailLinkDialog(false)}
                footer={<><Button label="Cancel" icon="pi pi-times" className="p-button-secondary" onClick={() => setResendMailLinkDialog(false)} /><Button label="Resend" icon="pi pi-check" className="common-primary-button" onClick={resendMailLinkSubmit} loading={resendMailLinkLoader} /></>}>
                <div className="grid">
                    <div className="field col-12">
                        <p className="mb-3 font-semibold">Are you sure you want to resend the sign document email?</p>
                        <div className="flex align-items-center mt-3">
                            <div className="flex align-items-center">
                                <Checkbox inputId="taxpayer_resend" checked={isTaxpayerResend} onChange={(e) => setIsTaxpayerResend(e.checked || false)} />
                                <label htmlFor="taxpayer_resend" className="ml-2">Taxpayer</label>
                            </div>
                            {((fileUploadData?.is_spouse_canadian_resident == 1 && fileUploadData?.file_spouse_tax == null && fileUploadData?.data_added_manually == 0) ||
                                (fileUploadData?.file_spouse_tax == 1 && fileUploadData?.data_added_manually == 1)) && (
                                    <div className="flex align-items-center ml-4">
                                        <Checkbox inputId="spouse_resend" checked={isSpouseResend} onChange={(e) => setIsSpouseResend(e.checked || false)} />
                                        <label htmlFor="spouse_resend" className="ml-2">Spouse</label>
                                    </div>
                                )}
                        </div>
                    </div>
                </div>
            </Dialog>

            {/* Modal to change filing dates */}
            <ChangeTaxFillingDateModal visible={taxFilingDateChangeDialog} onHide={() => setTaxFilingDateChangeDialog(false)} onConfirm={updateTaxFilingDate} loading={submitLoading} value={taxFilingDate} onChange={(e: any) => {
                const val = e.value;
                if (!val || !taxFilingDays) { setTaxFilingDate([]); return; }
                const startDate: Date = Array.isArray(val) ? val[val.length - 1] : val;
                const dates: Date[] = [];
                for (let i = 0; i < taxFilingDays; i++) {
                    const nextDate = new Date(startDate);
                    nextDate.setDate(startDate.getDate() + i);
                    dates.push(nextDate);
                }
                setTaxFilingDate(dates);
            }} errors={errors} />

            {/* Modal to edit taxpayer email */}
            <EditEmail visible={editEmailDialog} onHide={() => { setEditEmailDialog(false); setEmailErrors({}); }} data={globleEmailDialogData} errors={emailErrors} onChange={(e: any, name: string) => {
                setGlobleEmailDialogData({ ...globleEmailDialogData, [name]: (e.target && e.target.value) || '' });
            }} onUpdate={async () => {
                try {
                    const { isError, errors: valErrors } = validateEditEmail(globleEmailDialogData);
                    setEmailErrors(valErrors);
                    if (isError) return;
                    setSubmitLoading(true);
                    const response = await postAllData(UPDATE_TAXPAYER_EMAIL, { id: globleEmailDialogData.id, email: globleEmailDialogData.email });
                    if (response) {
                        setEditEmailDialog(false);
                        toast.current?.show({ severity: 'success', summary: 'Success', detail: response?.message });
                        getTaxFormList();
                    }
                } catch (error: any) {
                    toast.current?.show({ severity: 'error', summary: 'Error', detail: error?.response?.data?.message });
                } finally {
                    setSubmitLoading(false);
                }
            }} loading={submitLoading} />

            {/* Modal to confirm e-transfer */}
            <Dialog visible={isETransferModal} style={{ width: '450px' }} header="E-Transfer Payment Confirmation" modal onHide={() => setIsETransferModal(false)}
                footer={<><Button label="Cancel" icon="pi pi-times" className="p-button-secondary" onClick={() => setIsETransferModal(false)} /><Button label="Confirm" icon="pi pi-check" className="common-primary-button" onClick={eTransferPayment} loading={ETransferLoading} /></>}>
                <div>Are you sure you want to confirm E-Transfer payment?</div>
            </Dialog>

            {/* Modal to send required documents note email */}
            <Dialog
                visible={emailNoteDialog}
                style={{ width: '520px' }}
                header="Request Document Update"
                className="mx-3 lg:mx-0"
                draggable={false}
                modal
                footer={
                    <>
                        <Button
                            label="Cancel"
                            icon="pi pi-times"
                            className="p-button-secondary"
                            onClick={() => {
                                setEmailNoteDialog(false);
                                setEmailNoteText('');
                                setEmailNoteRowData(null);
                            }}
                        />
                        <Button
                            label="Send Email"
                            icon="pi pi-send"
                            className="common-primary-button"
                            loading={emailNoteLoading}
                            onClick={async () => {
                                if (!emailNoteText?.trim()) {
                                    toast.current?.show({ severity: 'warn', summary: 'Warning', detail: 'Please write the document update note before sending.' });
                                    return;
                                }
                                try {
                                    setEmailNoteLoading(true);
                                    const response = await updateData(SEND_DOCUMENT_NOTE_EMAIL, emailNoteRowData?.id, {
                                        email_note: emailNoteText.trim(),
                                    });
                                    if (response) {
                                        toast.current?.show({ severity: 'success', summary: 'Success', detail: response?.message || 'Document update note sent successfully.' });
                                        setEmailNoteDialog(false);
                                        setEmailNoteText('');
                                        setEmailNoteRowData(null);
                                        getTaxFormList();
                                    }
                                } catch (error: any) {
                                    toast.current?.show({ severity: 'error', summary: 'Error', detail: error?.response?.data?.message || 'Failed to send email.' });
                                } finally {
                                    setEmailNoteLoading(false);
                                }
                            }}
                        />
                    </>
                }
                onHide={() => {
                    setEmailNoteDialog(false);
                    setEmailNoteText('');
                    setEmailNoteRowData(null);
                }}
            >
                <div className="grid p-fluid">
                    <div className="field col-12">
                        <label htmlFor="email_note_textarea" className="font-semibold mb-2 block">
                            Document Update Note <span style={{ color: 'red' }}>*</span>
                        </label>
                        <InputTextarea
                            id="email_note_textarea"
                            value={emailNoteText}
                            onChange={(e) => setEmailNoteText(e.target.value)}
                            rows={6}
                            autoResize
                            placeholder="e.g. Please re-upload your T4 document, or provide the missing T5008..."
                            style={{ width: '100%', resize: 'vertical' }}
                        />
                    </div>
                </div>
            </Dialog>

            {/* Modal to show WhatsApp links */}
            <Dialog
                visible={whatAppLinkDialog}
                style={{ width: '500px' }}
                header="WhatsApp Link"
                className="mx-3 lg:mx-0"
                draggable={false}
                modal
                footer={
                    <>
                        <Button
                            label="Close"
                            icon="pi pi-times"
                            className="p-button-secondary"
                            onClick={hideWhatAppLinkDialog}
                        />
                    </>
                }
                onHide={hideWhatAppLinkDialog}
            >
                <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "10px" }}>
                    <CopyAllButton
                        data={whatAppLink}
                        onCopySuccess={handleCopyAllFinish}
                        type="individual"
                    />
                </div>
                <div className="grid p-fluid formgrid">
                    <div className="field col-12">
                        <p className="font-bold mb-0">T183 Document Url :</p>
                        <div className="input-effect-div flex gap-2">
                            {whatAppLink ? (
                                <>
                                    <a
                                        href={whatAppLink?.taxpayerSignUrl}
                                        target="_blank"
                                        rel="noreferrer"
                                        style={{
                                            color: '#2528d3',
                                            wordBreak: 'break-all',
                                            fontWeight: '600',
                                        }}
                                    >
                                        {whatAppLink?.taxpayerSignUrl}
                                    </a>
                                    <span
                                        style={{ cursor: 'pointer', display: 'flex' }}
                                        onClick={() => handleCopy(whatAppLink?.taxpayerSignUrl, null, "sign")}
                                        data-pr-tooltip={whastappLinkCopy ? 'Copied' : 'Copy'}
                                        data-pr-position="top"
                                    >
                                        {whastappLinkCopy ? (
                                            <Check size={16} color="#22c55e" />
                                        ) : (
                                            <Copy size={16} color="#6b7280" />
                                        )}
                                    </span>
                                    <Tooltip
                                        // target={`.${targetClass}`}
                                        key={whastappLinkCopy ? 'copied' : 'copy'} /></>

                            ) : (
                                <span style={{ color: '#6c757d' }}>
                                    No link available
                                </span>
                            )}
                        </div>
                        <p className="font-bold mb-0 mt-4">Tax Summary Document Url :</p>
                        <div className="input-effect-div flex gap-2">
                            {whatAppLink ? (
                                <>
                                    <a
                                        href={whatAppLink?.taxpayerReadUrl}
                                        target="_blank"
                                        rel="noreferrer"
                                        style={{
                                            color: '#2528d3',
                                            wordBreak: 'break-all',
                                            fontWeight: '600',
                                        }}
                                    >
                                        {whatAppLink?.taxpayerReadUrl}
                                    </a>

                                    <span
                                        style={{ cursor: 'pointer', display: 'flex' }}
                                        onClick={() => handleCopy(whatAppLink?.taxpayerReadUrl, null, "read")}
                                        data-pr-tooltip={whastappLinkReadDocCopy ? 'Copied' : 'Copy'}
                                        data-pr-position="top"
                                    >
                                        {whastappLinkReadDocCopy ? (
                                            <Check size={16} color="#22c55e" />
                                        ) : (
                                            <Copy size={16} color="#6b7280" />
                                        )}
                                    </span>
                                    <Tooltip
                                        // target={`.${targetClass}`}
                                        key={whastappLinkReadDocCopy ? 'copied' : 'copy'} /></>

                            ) : (
                                <span style={{ color: '#6c757d' }}>
                                    No link available
                                </span>
                            )}
                        </div>
                    </div>
                    {whatAppLink?.spouseSignUrl && (
                        <div className="field col-12">
                            <p className="font-bold mb-0">Spouse T183 Document Url :</p>
                            <div className="input-effect-div flex gap-2">
                                {whatAppLink ? (
                                    <>
                                        <a
                                            href={whatAppLink?.spouseSignUrl}
                                            target="_blank"
                                            rel="noreferrer"
                                            style={{
                                                color: '#2528d3',
                                                wordBreak: 'break-all',
                                                fontWeight: '600',
                                            }}
                                        >
                                            {whatAppLink?.spouseSignUrl}
                                        </a>
                                        <span
                                            style={{ cursor: 'pointer', display: 'flex' }}
                                            onClick={() => handleCopy(whatAppLink?.spouseSignUrl, null, "spouseSign")}
                                            data-pr-tooltip={whastappLinkSpouseCopy ? 'Copied' : 'Copy'}
                                            data-pr-position="top"
                                        >
                                            {whastappLinkSpouseCopy ? (
                                                <Check size={16} color="#22c55e" />
                                            ) : (
                                                <Copy size={16} color="#6b7280" />
                                            )}
                                        </span>
                                        <Tooltip
                                            key={whastappLinkSpouseCopy ? 'copied' : 'copy'} /></>

                                ) : (
                                    <span style={{ color: '#6c757d' }}>
                                        No link available
                                    </span>
                                )}
                            </div>
                            <p className="font-bold mb-0 mt-4">Spouse Tax Summary Document Url :</p>
                            <div className="input-effect-div flex gap-2">
                                {whatAppLink ? (
                                    <>
                                        <a
                                            href={whatAppLink?.spouseReadUrl}
                                            target="_blank"
                                            rel="noreferrer"
                                            style={{
                                                color: '#2528d3',
                                                wordBreak: 'break-all',
                                                fontWeight: '600',
                                            }}
                                        >
                                            {whatAppLink?.spouseReadUrl}
                                        </a>

                                        <span
                                            style={{ cursor: 'pointer', display: 'flex' }}
                                            onClick={() => handleCopy(whatAppLink?.spouseReadUrl, null, "spouseRead")}
                                            data-pr-tooltip={whastappLinkReadDocSpouseCopy ? 'Copied' : 'Copy'}
                                            data-pr-position="top"
                                        >
                                            {whastappLinkReadDocSpouseCopy ? (
                                                <Check size={16} color="#22c55e" />
                                            ) : (
                                                <Copy size={16} color="#6b7280" />
                                            )}
                                        </span>
                                        <Tooltip
                                            key={whastappLinkReadDocSpouseCopy ? 'copied' : 'copy'} /></>

                                ) : (
                                    <span style={{ color: '#6c757d' }}>
                                        No link available
                                    </span>
                                )}
                            </div>
                        </div>
                    )}
                    {whatAppLink?.e_transfer_payment && whatAppLink?.balance_amount && (
                        <div className="field col-12">
                            <p className="font-bold mb-0 mt-4">E-Transfer Note :</p>
                            <div className="input-effect-div flex gap-2">
                                <span>
                                    Please make a e-transfer of {whatAppLink?.balance_amount} dollars at as.nagra@gmail.com
                                </span>
                            </div>
                        </div>
                    )}
                </div>
            </Dialog >
        </>
    );
};

export default CommonTaxFileList;
